import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OnboardingModal from "@/components/onboarding/OnboardingModal";
import AgentOnboardingBanner from "@/components/onboarding/AgentOnboardingBanner";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { announceAgentOnboardingCompleted, onAgentOnboardingRequest } from "@/lib/agentOnboardingRequests";

// Routes where the skipped-agent nudge banner is allowed to appear. Under the
// legacy (flag-off) behaviour the wizard auto-opens regardless of route (so it
// reaches OAuth agents who land on /feed), but the persistent banner only
// lives on the two discovery surfaces. Under the bright UI (spec 2026-09-24
// §8) the wizard never auto-opens at all — see the `bright` branch below —
// and neither does this banner.
const BANNER_ROUTES = ["/explore", "/agent/explore", "/feed", "/agent/feed"];

// Auth utility routes where the recovery/OAuth session is authenticated but the
// user is mid-flow (e.g. setting a new password, or accepting an org-member
// invitation on /invite). The wizard must not pop over these surfaces even when
// onboarding is incomplete — on /invite the account becomes org-side only once
// accept_org_invitation creates its membership (org-side status is derived from
// current membership, #786), and the wizard would otherwise flash before that
// row exists on the Google/OAuth path. AcceptInvitePage then seeds the
// membership cache before navigating off /invite.
const SUPPRESS_AUTO_OPEN = ["/reset-password", "/auth/callback", "/invite", "/auth/social/callback"];
// Path prefixes with the same rule. /claim-organization/:slug: a visitor who
// just created an account there to claim a brokerage is mid-flow; the agent
// wizard would cover the claim form and, on completion, navigate to /explore.
const SUPPRESS_AUTO_OPEN_PREFIXES = ["/claim-organization/"];
const autoOpenSuppressed = (pathname: string) =>
  SUPPRESS_AUTO_OPEN.includes(pathname) || SUPPRESS_AUTO_OPEN_PREFIXES.some((p) => pathname.startsWith(p));

// Item 15 — single app-level mount that makes OnboardingModal reachable in
// production. Gated entirely by useOnboardingStatus (auth + profile + role +
// onboardingCompleted), so it is inert for org admins and signed-out users.
const AgentOnboardingGate = () => {
  const { settled, canOnboard, needsOnboarding, markCompleted } = useOnboardingStatus();
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  // Auto-open at most once per session — after a skip we must not re-open on
  // every navigation; the banner takes over the nudging.
  // Seeded from sessionStorage so a full-page reload within the same tab session
  // does not re-open after a skip.
  const [autoOpened, setAutoOpened] = useState(
    () => sessionStorage.getItem("compaire_agent_onboarding_autoopened") === "1"
  );

  // Staff accounts on the internal Console can carry the legacy agent role, but
  // the wizard must never surface there — Console is not an agent surface. The
  // real fix (staff accounts not carrying the agent role) is tracked separately.
  const isConsoleRoute = location.pathname.startsWith("/console");

  // Bright agent UI (spec 2026-09-24 §8): no auto-open and no bottom banner;
  // the wizard opens from the Explore fit card, the story panel and Settings.
  const bright = FEATURE_FLAGS.AGENT_UI_V2;
  // True while the wizard is open because a surface asked for it, so
  // completion leaves the agent where they were.
  const openedByRequest = useRef(false);

  useEffect(
    () =>
      onAgentOnboardingRequest(() => {
        // Every requesting surface (the Explore fit card, "Set preferences",
        // Settings) is itself gated behind AGENT_UI_V2, but the bus is a
        // global window event with no flag awareness of its own — honour a
        // request only when the bright UI is on, and only for an account
        // actually eligible for this wizard (the hook's own rule, not the
        // raw role flag) that is not on a Console route.
        if (!bright || !canOnboard || isConsoleRoute) return;
        openedByRequest.current = true;
        setIsOpen(true);
      }),
    [bright, canOnboard, isConsoleRoute],
  );

  useEffect(() => {
    const isPreview = new URLSearchParams(location.search).get("preview") === "true";
    if (
      !bright &&
      needsOnboarding &&
      !autoOpened &&
      !autoOpenSuppressed(location.pathname) &&
      !isConsoleRoute &&
      !isPreview
    ) {
      setIsOpen(true);
      setAutoOpened(true);
      sessionStorage.setItem("compaire_agent_onboarding_autoopened", "1");
    }
  }, [bright, needsOnboarding, autoOpened, location.pathname, location.search, isConsoleRoute]);

  const handleComplete = () => {
    // The wizard's atomic write (preferences + onboardingCompleted=true) has
    // landed and PR #169's fit-score fan-call has fired. Reflect completion
    // locally so the banner won't reappear, tell other surfaces, then
    // navigate to surface the now-populated matches — unless a surface
    // requested it, in which case the agent stays exactly where they were.
    markCompleted();
    announceAgentOnboardingCompleted();
    if (openedByRequest.current) {
      openedByRequest.current = false;
      setIsOpen(false);
      return;
    }
    navigate("/explore");
  };

  const showBanner =
    !bright && settled && needsOnboarding && !isOpen && BANNER_ROUTES.includes(location.pathname);

  return (
    <>
      <OnboardingModal
        isOpen={isOpen && !isConsoleRoute}
        onClose={() => {
          openedByRequest.current = false;
          setIsOpen(false);
        }}
        onComplete={handleComplete}
        bright={bright}
      />
      {showBanner && <AgentOnboardingBanner onStart={() => setIsOpen(true)} />}
    </>
  );
};

export default AgentOnboardingGate;
